<script setup lang="ts">
import { ClipboardDocumentCheckIcon, ClipboardDocumentIcon } from "@heroicons/vue/24/outline";
import { useTimeoutFn } from "@vueuse/core";
import { ref } from "vue";

const props = defineProps<{
  value: string;
  successMessage: string;
}>();

const showCopied = ref(false);

const { start: startTimeout } = useTimeoutFn(() => {
  showCopied.value = false;
}, 2000);

async function copyToClipboard() {
  try {
    await navigator.clipboard.writeText(props.value);
    showCopied.value = true;
    startTimeout();
  } catch (err) {
    console.error("Failed to copy text: ", err);
  }
}
</script>

<template>
  <div class="relative mx-auto w-full">
    <span
      class="text-primary absolute -top-5 -right-5 z-[1] flex size-10 items-center justify-center overflow-hidden rounded-full bg-white shadow-sm"
    >
      <Transition name="slide" mode="out-in">
        <ClipboardDocumentIcon v-if="!showCopied" class="size-5" />

        <ClipboardDocumentCheckIcon v-else class="size-5" />
      </Transition>
    </span>

    <button
      class="text-primary relative w-full cursor-pointer overflow-hidden rounded-md bg-gray-100 px-6 py-4 text-left text-xl font-semibold transition-colors"
      @click="copyToClipboard"
    >
      <span class="block truncate text-center">{{ value }}</span>

      <div
        v-if="showCopied"
        class="animate-slide-in bg-galbani absolute inset-0 flex items-center justify-center rounded-md text-white"
      >
        {{ successMessage }}
      </div>
    </button>
  </div>
</template>

<style scoped>
@keyframes slideIn {
  from {
    transform: translateX(-100%);
  }
  to {
    transform: translateX(0);
  }
}

.animate-slide-in {
  animation: slideIn 0.2s ease-out;
}

.slide-enter-active,
.slide-leave-active {
  transition: all 0.2s ease-out;
}

.slide-enter-from {
  transform: translateY(-50%);
  opacity: 0;
}

.slide-leave-to {
  transform: translateY(50%);
  opacity: 0;
}

.slide-enter-to,
.slide-leave-from {
  transform: translateY(0);
  opacity: 1;
}
</style>
